import { IntlProvider } from 'react-intl';
import './App.css';
import AuthenticatedApp from './components/AuthenticatedApp/AuthenticatedApp';
import { IntakeLeftMenu } from './components/IntakeLeftMenu/IntakeLeftMenu';
import EnglishMessages from './lang/en.json';
import SpanishMessages from './lang/es.json';
import FrenchMessages from './lang/fr.json';
import { useLanguage } from './contexts/LanguageProvider';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useHeaderContext } from './contexts/HeaderContext';
import { useAppUser } from './contexts/AppUserProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthErrorHandler from './components/AuthErrorHandler/AuthErrorHandler';
import { MobileBottomNav } from './components/MobileBottomNav/MobileBottomNav';
import { RecordingNavigationGuard } from './components/RecordingNavigationGuard';
import { useMobileRecording } from './contexts/MobileRecordingContext';
import { useRecordingNavigationGuard } from './hooks/useRecordingNavigationGuard';

const messagesMap: Record<string, Record<string, string>> = {
  en: EnglishMessages,
  es: SpanishMessages,
  fr: FrenchMessages
};

const PENDING_INVITE_TOKEN_KEY = 'pending_invite_token';

function App() {
  const { locale } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { headerText, menuActions } = useHeaderContext();
  const {
    isMobileRecordingMode,
    isPaused,
    onPauseRecording,
    onResumeRecording,
    onGenerateSummary
  } = useMobileRecording();
  const {
    appUser,
    selectedUserRole,
    isLoading: appUserLoading,
    authError,
    canRetryAuth
  } = useAppUser();
  const { isLoading: auth0Loading, isAuthenticated } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  // Identify routes that render the NewTranscriptPageV4
  const isTranscriptV4Route =
    location.pathname.startsWith('/transcript/') &&
    !location.pathname.includes('/self-intake');
  const isLegacyTemplateRoute = location.pathname.startsWith('/new-intake/');
  const isTranscriptPage = isTranscriptV4Route || isLegacyTemplateRoute;

  // Check if we're on pages that shouldn't show the FAB
  const isAiChatPage = location.pathname === '/ai-chat';
  const isTranscriptDetailPage = location.pathname.startsWith('/transcript/');
  const shouldHideFab = isAiChatPage || isTranscriptDetailPage;

  // Check if we're on a recording page to show recording controls
  const isOnRecordingPage =
    location.pathname.startsWith('/transcript/') &&
    !location.pathname.includes('/self-intake');

  // State for floating recording container visibility
  const [showFloatingRecording, setShowFloatingRecording] = useState(false);

  // Recording state for navigation guard - works on both mobile and desktop
  // TODO: Get actual recording state from recording context instead of just mobile mode
  // For now: Mobile uses mobile recording mode, desktop assumes recording if on recording page
  const isCurrentlyRecording =
    isOnRecordingPage && (isMobile ? isMobileRecordingMode : false); // Disabled for desktop until proper recording state is available

  // Placeholder functions for recording actions - TODO: Connect to actual recording logic
  const handleEndRecording = async () => {
    console.log('Ending recording and saving...');
    // TODO: Stop recording, generate summary, save session
  };

  const handleDiscardRecording = async () => {
    console.log('Discarding recording...');
    // TODO: Stop recording, discard session data
  };

  // Navigation guard for recording
  const {
    showGuard,
    isProcessing,
    handleContinueRecording,
    handleEndAndSave,
    handleDiscard,
    guardedNavigate
  } = useRecordingNavigationGuard({
    isRecording: isCurrentlyRecording,
    onEndRecording: handleEndRecording,
    onDiscardRecording: handleDiscardRecording,
    recordingDuration: undefined // TODO: Get actual duration
  });

  // Listen for custom event to show floating recording container
  useEffect(() => {
    const handleShowFloatingRecording = () => {
      setShowFloatingRecording(true);
    };

    window.addEventListener(
      'showFloatingRecording',
      handleShowFloatingRecording
    );

    return () => {
      window.removeEventListener(
        'showFloatingRecording',
        handleShowFloatingRecording
      );
    };
  }, []);

  // Close any transient overlays when switching between mobile and desktop to avoid stuck backdrops
  useEffect(() => {
    // Close local drawers
    // Ensure body scroll lock is cleared
    try {
      document.body.style.overflow = '';
      document.body.style.cursor = 'default';
      document.body.classList.remove('resizing');
    } catch {}
    // Dispatch Escape to close any open MUI/Radix modals/popovers/drawers
    const escEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true
    });
    document.dispatchEvent(escEvent);
    const escUpEvent = new KeyboardEvent('keyup', {
      key: 'Escape',
      code: 'Escape',
      keyCode: 27,
      which: 27,
      bubbles: true
    });
    document.dispatchEvent(escUpEvent);

    // As a safeguard, remove any lingering backdrops/overlays created by portals
    const removeOverlays = () => {
      try {
        // MUI backdrops
        document
          .querySelectorAll('.MuiBackdrop-root')
          .forEach((el) => el.parentElement?.removeChild(el));
        // Radix/Shadcn dialog overlays
        document
          .querySelectorAll('[data-slot="dialog-overlay"]')
          .forEach((el) => el.parentElement?.removeChild(el));
        // Generic tailwind-based full-screen overlays (fixed + inset-0 + high z)
        document
          .querySelectorAll(
            'div[class*="fixed"][class*="inset-0"][class*="z-50"]'
          )
          .forEach((el) => {
            // Avoid removing our main app container or known UI (no data-slot, no MUI classes)
            const htmlEl = el as HTMLElement;
            const cls = htmlEl.className || '';
            if (
              !htmlEl.id &&
              !cls.includes('content-scroll-container') &&
              !cls.includes('MuiDrawer') &&
              !cls.includes('MuiAppBar')
            ) {
              el.parentElement?.removeChild(el);
            }
          });
      } catch {}
    };
    removeOverlays();
    // Attempt again on next tick and after transitions
    setTimeout(removeOverlays, 0);
    setTimeout(removeOverlays, 300);
  }, [isMobile]);

  // Ref to track if the initial role has been processed after loading
  const hasInitializedRef = useRef(false);
  // Ref to store the ID of the previously selected role
  const previousRoleIdRef = useRef(
    selectedUserRole ? selectedUserRole.id : null
  );

  useEffect(() => {
    // Don't proceed if Auth0 or AppUser data is still loading.
    // This prevents premature actions before the initial state is settled.
    if (auth0Loading || appUserLoading) {
      return;
    }

    const currentRoleId = selectedUserRole ? selectedUserRole.id : null;

    // Check if the initial state has been processed.
    if (hasInitializedRef.current) {
      // If initialized, and the current role ID is different from the previously stored role ID,
      // it means the role has actually changed.
      if (previousRoleIdRef.current !== currentRoleId) {
        console.log(
          `Role changed from ${previousRoleIdRef.current} to ${currentRoleId}. Reloading page...`
        );
        window.location.reload();
      }
    } else {
      // This block runs once after auth0Loading and appUserLoading are false.
      // It marks that the initial state (including the initial selectedUserRole) has been processed.
      hasInitializedRef.current = true;
    }

    // After all logic, update the previousRoleIdRef to the current role's ID
    // for the next time the effect runs.
    previousRoleIdRef.current = currentRoleId;
  }, [selectedUserRole, auth0Loading, appUserLoading]); // Dependencies for the effect

  // Check if we're on pages that should be accessible without authentication
  const isAcceptInvitationPage = location.pathname === '/accept-invitation';
  const isPublicMagicLinkPage =
    location.pathname.startsWith('/l/') ||
    location.pathname.startsWith('/share-link/customer-document/');
  const isPublicStandalonePage =
    isAcceptInvitationPage || isPublicMagicLinkPage;

  // Ensure users with a pending invitation token get routed to the accept invitation page
  useEffect(() => {
    if (auth0Loading) {
      return;
    }

    const pendingToken = localStorage.getItem(PENDING_INVITE_TOKEN_KEY);
    if (pendingToken && isAuthenticated && !isPublicStandalonePage) {
      navigate('/accept-invitation', { replace: true });
    }
  }, [auth0Loading, isAuthenticated, isPublicStandalonePage, navigate]);

  return (
    <IntlProvider locale={locale} messages={messagesMap[locale]}>
      {/* Root container with flex layout and fixed dimensions */}
      <Box
        sx={{
          display: 'flex',
          position: 'fixed', // Changed from 'absolute' to 'fixed'
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          overflow: 'hidden' // Prevent scrolling at the root level
        }}
      >
        {!isPublicStandalonePage && <IntakeLeftMenu />}

        {isPublicStandalonePage ? (
          // Render public standalone pages without authentication checks
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.background.paper,
              padding: 2
            }}
          >
            <AuthenticatedApp />
          </Box>
        ) : appUser && selectedUserRole ? (
          // Main content area with independent scrolling
          <Box
            key={isMobile ? 'mobile-layout' : 'desktop-layout'}
            className="content-scroll-container"
            sx={{
              flexGrow: 1,
              backgroundColor: theme.palette.background.default,
              overflow: isTranscriptPage || isAiChatPage ? 'hidden' : 'auto', // Disable outer scroll on transcript & AI chat pages
              height: '100%', // Take full height
              WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
              overscrollBehavior: isTranscriptPage ? 'none' : 'auto',

              // Handle scrollbar visibility based on device
              ...(isMobile
                ? {
                    // Always show scrolling functionality on mobile, but hide scrollbar
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    }
                  }
                : {
                    // On desktop, hide scrollbar by default, show on hover
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      backgroundColor: 'transparent',
                      display: 'none'
                    },
                    '&:hover::-webkit-scrollbar': {
                      display: 'block'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor:
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.2)'
                          : 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '4px'
                    }
                  })
            }}
          >
            <AppBar
              id="main-appbar"
              elevation={0}
              position="sticky"
              sx={{
                display: 'flex',
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
                mb: 0
              }}
            >
              <Toolbar
                sx={{
                  px: isMobile ? 2 : 2, // Changed from 4 to 2 (16px instead of 32px on desktop)
                  alignItems: 'center',
                  minHeight: isMobile ? '56px' : '64px',
                  overflow: 'visible', // Allow overflow when editing title
                  position: 'relative'
                }}
              >
                {isMobile ? (
                  // Mobile: Layout with space for hamburger menu and truncated title
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingLeft: '56px', // Space for hamburger menu (48px icon + 8px margin)
                      paddingRight: 0,
                      minWidth: 0,
                      gap: 1,
                      overflow: 'visible', // Allow overflow when editing title
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="div"
                      sx={{
                        fontSize: typeof headerText === 'string' ? '16px' : '18px',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        overflow: 'visible', // Allow overflow when editing title
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        textAlign: 'left',
                        paddingRight: 1, // Small padding before menu button
                        minWidth: 0,
                        color: theme.palette.text.primary
                      }}
                    >
                      {headerText}
                    </Box>
                    {menuActions && (
                      <Box sx={{ flexShrink: 0, ml: 'auto' }}>
                        {menuActions}
                      </Box>
                    )}
                  </Box>
                ) : (
                  // Desktop: Original layout
                  <>
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingLeft: 0,
                        minWidth: 0,
                        overflow: 'visible', // Allow overflow when editing title
                        position: 'relative'
                      }}
                    >
                      <Box
                        component="div"
                        sx={{
                          fontSize: typeof headerText === 'string' ? '16px' : '20px',
                          fontWeight: 600,
                          lineHeight: 1.3,
                          minWidth: 0,
                          overflow: 'visible', // Allow overflow when editing title
                          color: theme.palette.text.primary
                        }}
                      >
                        {headerText}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0
                      }}
                    >
                      {menuActions}
                    </Box>
                  </>
                )}
              </Toolbar>
            </AppBar>
            {/* Content wrapper without extra padding */}
            <Box
              sx={{
                padding: 0,
                // Add extra bottom padding on mobile to account for bottom nav
                paddingBottom: isMobile
                  ? isTranscriptPage || isAiChatPage
                    ? 0
                    : '84px'
                  : 0
              }}
            >
              <AuthenticatedApp />

              {/* Mobile Bottom Navigation - Always Normal */}
              <MobileBottomNav
                isRecording={isOnRecordingPage && isMobileRecordingMode}
                isPaused={isPaused}
                showFloatingRecording={showFloatingRecording}
                onShowRecordingContainer={() => setShowFloatingRecording(true)}
                guardedNavigate={guardedNavigate}
              />

              {/* Recording Navigation Guard */}
              <RecordingNavigationGuard
                open={showGuard}
                onContinueRecording={handleContinueRecording}
                onEndAndSave={handleEndAndSave}
                onDiscard={handleDiscard}
                isProcessing={isProcessing}
                recordingDuration={undefined} // TODO: Get actual duration
              />
            </Box>
          </Box>
        ) : (
          // Message for no selected user role
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.background.paper,
              padding: 2
            }}
          >
            {appUser || appUserLoading || auth0Loading ? (
              auth0Loading || appUserLoading ? (
                <Typography variant="h6" color="text.secondary">
                  Loading...
                </Typography>
              ) : authError || canRetryAuth ? (
                <AuthErrorHandler />
              ) : (
                <Typography variant="h6" color="text.secondary">
                  No Selected Role...
                </Typography>
              )
            ) : (
              <AuthenticatedApp />
            )}
          </Box>
        )}
      </Box>
    </IntlProvider>
  );
}

export default App;
